import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import {
  getCurrentFestival, subscribeFestival, isFestiveVibeOn, setFestiveVibeOn,
  isGreetingOpen, isLoginGreetingPending,
} from '../../utils/festivals';
import { runAmbient } from './ambientEffects';

const RECHECK_MS = 10 * 60 * 1000; // pick up a festival starting/ending during a long session
const PENDING_GRACE_MS = 10000;    // don't wait forever for a login greeting that never mounts

// Subtle festival theme that stays on while people work (garland, corner sparks,
// edge snow, ...). Mount once per layout. It never takes clicks (pointer-events: none)
// and sits at z-30, below dropdowns, drawers and modals (z-40 and up).
// It waits while the login greeting is showing, then fades in as that fades out.
export default function FestivalAmbient() {
  const [festival, setFestival] = useState(getCurrentFestival);
  const [graceOver, setGraceOver] = useState(false);
  const [visible, setVisible] = useState(false);
  const canvasRef = useRef(null);

  const vibeOn = useSyncExternalStore(subscribeFestival, isFestiveVibeOn);
  const greetingOpen = useSyncExternalStore(subscribeFestival, isGreetingOpen);
  const loginPending = useSyncExternalStore(subscribeFestival, isLoginGreetingPending);

  useEffect(() => {
    const id = setInterval(() => setFestival(getCurrentFestival()), RECHECK_MS);
    const grace = setTimeout(() => setGraceOver(true), PENDING_GRACE_MS);
    return () => { clearInterval(id); clearTimeout(grace); };
  }, []);

  const waiting = greetingOpen || (loginPending && !graceOver);
  const active = Boolean(festival?.ambient) && vibeOn && !waiting;
  const type = festival?.ambient;
  const palette = festival?.palette;

  useEffect(() => {
    if (!active || !canvasRef.current) return;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const stop = runAmbient(canvasRef.current, type, { palette, still });
    const fade = setTimeout(() => setVisible(true), 30);
    return () => { stop(); clearTimeout(fade); setVisible(false); };
  }, [active, type, palette]);

  if (!festival?.ambient || waiting) return null;

  return createPortal(
    <>
      {active && (
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          className="fixed inset-0 w-full h-full pointer-events-none z-30"
          style={{ opacity: visible ? 1 : 0, transition: 'opacity 1.2s ease' }}
        />
      )}
      <button
        type="button"
        onClick={() => setFestiveVibeOn(!vibeOn)}
        title={vibeOn ? 'Turn off festive vibe' : 'Turn on festive vibe'}
        aria-label={vibeOn ? 'Turn off festive vibe' : 'Turn on festive vibe'}
        aria-pressed={vibeOn}
        className={`fixed right-3 bottom-20 sm:bottom-4 z-30 w-9 h-9 rounded-full flex items-center justify-center
                   bg-white/80 border border-gray-200 shadow-sm backdrop-blur text-lg
                   hover:opacity-100 transition-opacity ${vibeOn ? 'opacity-60' : 'opacity-40 grayscale'}`}
      >
        🎆
      </button>
    </>,
    document.body,
  );
}
